/**
 * MF Variant Availability Interest V1
 * Show the restock (Notify me) block only when a complete selected variant exists
 * and available === false. Remount-safe: document / product-info scoped
 * listeners (not variant-selects). No duplicate binds.
 * STOQ V2 (Capture QA): submit is intercepted and sent to STOQ's documented
 * keyless storefront endpoint (POST https://app.stoqapp.com/api/v1/intents.json,
 * header X-Shopify-Shop-Domain). The native contact form POST (Shopify contact
 * inbox) is no longer used. Color/size are implied by the variant id (STOQ v1
 * has no free-form field). Markup, CSS and show/hide logic unchanged.
 * Legacy return_to / contact_posted handling kept for compatibility only.
 */
(function () {
  'use strict';

  var ROOT_SEL = '[data-mf-variant-interest]';
  var bound = false;
  var successByRoot = new WeakMap();

  function qsa(scope, sel) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function qs(scope, sel) {
    return (scope || document).querySelector(sel);
  }

  function parseOptionNames(root) {
    var raw = root.getAttribute('data-option-names') || '[]';
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function colorSizeFromVariant(root, variant) {
    var names = parseOptionNames(root);
    var color = '';
    var size = '';
    if (!variant) return { color: color, size: size };

    var values = variant.options;
    if (!values || !values.length) {
      values = [variant.option1, variant.option2, variant.option3].filter(function (v) {
        return v != null && v !== '';
      });
    }

    for (var i = 0; i < names.length; i++) {
      var n = String(names[i] || '').toLowerCase();
      var v = values[i] != null ? String(values[i]) : '';
      if (n === 'color' || n === 'colour') color = v;
      else if (n === 'size') size = v;
    }
    return { color: color, size: size };
  }

  function buildBody(productTitle, productId, color, size, variantId) {
    return (
      'Variant availability interest\n\n' +
      'Product: ' +
      (productTitle || '') +
      ' (ID: ' +
      (productId || '') +
      ')\n' +
      'Color: ' +
      (color || '') +
      '\n' +
      'Size: ' +
      (size || '') +
      '\n' +
      'Variant ID: ' +
      (variantId || '') +
      '\n'
    );
  }

  function setField(root, sel, value) {
    var el = qs(root, sel);
    if (!el) return;
    el.value = value != null ? String(value) : '';
  }

  function productBaseUrl(root) {
    var fromData = root.getAttribute('data-product-url') || '';
    if (fromData) return fromData;
    try {
      return window.location.pathname || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Shopify sets form.posted_successfully? from ?contact_posted=true.
   * Custom return_to with only ?variant= preserves the OOS variant (working) but
   * does NOT get contact_posted merged in — Liquid then keeps the email/CTA UI.
   * Bake both params into return_to so POST/reload shows the success state
   * on the same selected variant. Native POST/reload unchanged (no AJAX).
   */
  function syncReturnTo(root, variantId) {
    var form = qs(root, 'form.mf-variant-interest__form') || qs(root, 'form');
    if (!form) return;
    var input = qs(form, 'input[name="return_to"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'return_to';
      form.appendChild(input);
    }
    var base = productBaseUrl(root);
    if (!base) return;
    var id = variantId != null && variantId !== '' ? String(variantId) : '';
    var q = [];
    if (id) q.push('variant=' + encodeURIComponent(id));
    q.push('contact_posted=true');
    input.value = base + '?' + q.join('&');
  }

  function urlIndicatesContactPosted() {
    try {
      return new URLSearchParams(window.location.search).get('contact_posted') === 'true';
    } catch (e) {
      return false;
    }
  }

  function urlVariantParam() {
    try {
      return new URLSearchParams(window.location.search).get('variant') || '';
    } catch (e) {
      return '';
    }
  }

  function postedVariantStorageKey(root) {
    return 'mfViPostedVariant:' + (root.getAttribute('data-product-id') || 'x');
  }

  function rememberPostedVariant(root, variantId) {
    if (variantId == null || variantId === '') return;
    try {
      sessionStorage.setItem(postedVariantStorageKey(root), String(variantId));
    } catch (e) {}
  }

  function readPostedVariant(root) {
    try {
      return sessionStorage.getItem(postedVariantStorageKey(root)) || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Apply the success state only for the variant that was actually posted.
   * contact_posted can linger in the URL after Dawn updates ?variant= on change;
   * binding success to the first posted variant id prevents a different OOS
   * variant from incorrectly inheriting the success state (req).
   */
  function applyPostedSuccessState(root, variant) {
    var successEl = qs(root, '[data-mf-vi-success]');
    var liquidSuccess = !!(successEl && !successEl.hidden);
    var urlPosted = urlIndicatesContactPosted();
    if (!liquidSuccess && !urlPosted) return;

    var existing = readPostedVariant(root);
    if (liquidSuccess) {
      var id = variant && variant.id != null ? String(variant.id) : urlVariantParam();
      rememberPostedVariant(root, id);
    } else if (urlPosted && !existing) {
      rememberPostedVariant(root, urlVariantParam() || (variant && variant.id));
    }

    var bound = readPostedVariant(root);
    var currentId = variant && variant.id != null ? String(variant.id) : '';
    if (bound && currentId && bound === currentId && !root.hidden) {
      showSuccess(root, true);
    }
  }

  function syncHiddenFields(root, variant) {
    if (!variant) return;
    var productId = root.getAttribute('data-product-id') || '';
    var productTitle = root.getAttribute('data-product-title') || '';
    var mapped = colorSizeFromVariant(root, variant);

    setField(root, '[data-mf-vi-product-id]', productId);
    setField(root, '[data-mf-vi-product-title]', productTitle);
    setField(root, '[data-mf-vi-variant-id]', variant.id);
    setField(root, '[data-mf-vi-color]', mapped.color);
    setField(root, '[data-mf-vi-size]', mapped.size);
    setField(
      root,
      '[data-mf-vi-body]',
      buildBody(productTitle, productId, mapped.color, mapped.size, variant.id)
    );
    syncReturnTo(root, variant.id);
  }

  function showSuccess(root, on) {
    var success = qs(root, '[data-mf-vi-success]');
    var fields = qs(root, '[data-mf-vi-fields]');
    if (success) success.hidden = !on;
    if (fields) fields.hidden = !!on;
    successByRoot.set(root, !!on);
  }

  function applyVisibility(root, variant) {
    var show = !!(variant && variant.available === false);
    root.hidden = !show;
    root.setAttribute('aria-hidden', show ? 'false' : 'true');

    if (variant) {
      syncHiddenFields(root, variant);
    }

    if (show) {
      // Switching unavailable→unavailable updates context and resets success UI.
      var lastId = root.getAttribute('data-mf-vi-last-variant') || '';
      var nextId = String(variant.id);
      if (lastId && lastId !== nextId) {
        showSuccess(root, false);
        setSuccessCopy(root, false);
        clearError(root);
      }
      root.setAttribute('data-mf-vi-last-variant', nextId);
    } else {
      root.removeAttribute('data-mf-vi-last-variant');
    }
  }

  function readSelectedVariant(productInfo) {
    if (!productInfo) return null;
    var script =
      qs(productInfo, 'variant-selects [data-selected-variant]') ||
      qs(productInfo, '[data-selected-variant]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function syncRoot(root, variant) {
    applyVisibility(root, variant);
  }

  function boot(scope) {
    var roots = qsa(scope || document, ROOT_SEL);
    roots.forEach(function (root) {
      var pi = root.closest('product-info') || qs(document, 'product-info');
      var variant = readSelectedVariant(pi);
      // Prefer live selected variant; fall back to hidden input id + availability
      // is already encoded in data-selected-variant when complete.
      syncRoot(root, variant);
      ensureReturnToOnSubmit(root);

      // Success from Liquid (posted_successfully?) and/or ?contact_posted=true,
      // bound to the posted variant id so a later OOS selection does not inherit it.
      applyPostedSuccessState(root, variant);
    });
  }

  function onVariantChange(event) {
    var data = event && event.data ? event.data : null;
    var variant = data ? data.variant : null;
    // null variant = incomplete selection → hide
    var sectionId = data ? data.sectionId : null;
    qsa(document, ROOT_SEL).forEach(function (root) {
      if (sectionId && root.getAttribute('data-section') && root.getAttribute('data-section') !== String(sectionId)) {
        return;
      }
      syncRoot(root, variant || null);
    });
  }

  var STOQ_ENDPOINT = 'https://app.stoqapp.com/api/v1/intents.json';
  var STOQ_SHOP = 'marble-fangs.myshopify.com';
  var MSG_ERROR = 'Something went wrong. Please try again.';
  var MSG_EMAIL = 'Enter a valid email address.';
  var MSG_ALREADY = 'You’re already registered for this size.';

  function clearError(root) {
    qsa(root, '[data-mf-vi-js-error]').forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });
  }

  function showError(root, msg) {
    clearError(root);
    var fields = qs(root, '[data-mf-vi-fields]');
    if (!fields) return;
    var el = document.createElement('div');
    el.className = 'mf-variant-interest__error';
    el.setAttribute('role', 'alert');
    el.setAttribute('data-mf-vi-js-error', '');
    el.textContent = msg;
    var anchor = qs(fields, '[data-mf-vi-interest-type]') || qs(fields, '.mf-variant-interest__row');
    fields.insertBefore(el, anchor || null);
  }

  function successHeadingEl(root) {
    return qs(root, '[data-mf-vi-success-heading]');
  }

  function setSuccessCopy(root, already) {
    var heading = successHeadingEl(root);
    if (!heading) return;
    if (!heading.hasAttribute('data-mf-vi-default-copy')) {
      heading.setAttribute('data-mf-vi-default-copy', heading.textContent);
    }
    heading.textContent = already ? MSG_ALREADY : heading.getAttribute('data-mf-vi-default-copy');
  }

  function currentVariantId(root) {
    var vidEl = qs(root, '[data-mf-vi-variant-id]');
    var vid = vidEl ? vidEl.value : '';
    if (!vid) {
      var pi = root.closest('product-info') || qs(document, 'product-info');
      var live = readSelectedVariant(pi);
      if (live && live.id) vid = live.id;
    }
    return vid;
  }

  function submitToStoq(root, form) {
    var emailEl = qs(form, 'input[name="contact[email]"]');
    var submitEl = qs(form, '.mf-variant-interest__submit');
    var email = emailEl ? String(emailEl.value || '').trim() : '';
    var variantId = parseInt(currentVariantId(root), 10);
    var productId = parseInt(root.getAttribute('data-product-id') || '', 10);

    if (!email || (emailEl && emailEl.checkValidity && !emailEl.checkValidity())) {
      showError(root, MSG_EMAIL);
      return;
    }
    if (!variantId || !productId) {
      showError(root, MSG_ERROR);
      return;
    }
    if (form.getAttribute('data-mf-vi-submitting') === '1') return;
    form.setAttribute('data-mf-vi-submitting', '1');
    if (submitEl) submitEl.disabled = true;
    clearError(root);

    var payload = {
      intent: {
        shopify_variant_id: variantId,
        shopify_product_id: productId,
        channel: 'email',
        quantity: 1,
        source: 'api'
      },
      customer: {
        email: email,
        accepts_marketing: false
      }
    };

    fetch(STOQ_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Shop-Domain': STOQ_SHOP },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            return { status: res.status, data: data || {} };
          });
      })
      .then(function (r) {
        var errors = (r.data && r.data.errors) || {};
        var created = r.status >= 200 && r.status < 300 && r.data && r.data.intents && r.data.intents.length;
        if (errors.already_registered) {
          setSuccessCopy(root, true);
          showSuccess(root, true);
        } else if (created) {
          setSuccessCopy(root, false);
          showSuccess(root, true);
        } else if (errors.email) {
          showError(root, MSG_EMAIL);
          return;
        } else {
          showError(root, MSG_ERROR);
          return;
        }
        rememberPostedVariant(root, variantId);
        var successEl = qs(root, '[data-mf-vi-success]');
        if (successEl) {
          successEl.setAttribute('tabindex', '-1');
          try {
            successEl.focus({ preventScroll: true });
          } catch (e) {}
        }
      })
      .catch(function () {
        showError(root, MSG_ERROR);
      })
      .then(function () {
        form.removeAttribute('data-mf-vi-submitting');
        if (submitEl) submitEl.disabled = false;
      });
  }

  function ensureReturnToOnSubmit(root) {
    var form = qs(root, 'form.mf-variant-interest__form') || qs(root, 'form');
    if (!form || form.getAttribute('data-mf-vi-return-bound') === '1') return;
    form.setAttribute('data-mf-vi-return-bound', '1');
    form.addEventListener('submit', function (ev) {
      // STOQ: never post to the Shopify contact inbox.
      ev.preventDefault();
      submitToStoq(root, form);
    });
  }

  function bindOnce() {
    if (bound) return;
    bound = true;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    }

    // Path A full product-info swap — re-sync roots inside new DOM
    document.addEventListener('product-info:loaded', function (ev) {
      var host = ev && ev.target && ev.target.querySelector ? ev.target : document;
      boot(host);
    });
  }

  bindOnce();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }
})();
